import { useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { DayPicker } from 'react-day-picker';
import { format } from 'date-fns';
import { CalendarIcon, Clock } from 'lucide-react';
import 'react-day-picker/style.css';

interface DateTimePickerProps {
  value: Date;
  onChange: (date: Date) => void;
  label?: string;
  minDate?: Date;
  maxDate?: Date;
  showTime?: boolean;
  className?: string;
}

export const DateTimePicker: React.FC<DateTimePickerProps> = ({
  value,
  onChange,
  label,
  minDate,
  maxDate,
  showTime = false,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleDateSelect = (date: Date | undefined) => {
    if (date) {
      const newDate = new Date(date);
      // Preserve time if showTime is enabled
      if (showTime) {
        newDate.setHours(value.getHours(), value.getMinutes(), value.getSeconds());
      }
      onChange(newDate);
      if (!showTime) {
        setIsOpen(false);
      }
    }
  };

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const [hours, minutes] = e.target.value.split(':').map(Number);
    const newDate = new Date(value);
    newDate.setHours(hours, minutes, 59, 999);
    onChange(newDate);
  };

  const displayValue = showTime
    ? format(value, 'MMM d, yyyy HH:mm')
    : format(value, 'MMM d, yyyy');

  const timeValue = format(value, 'HH:mm');

  return (
    <div className={className}>
      {label && (
        <label className="block text-xs font-medium text-gray-600 mb-1">
          {label}
        </label>
      )}

      <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
        <Popover.Trigger asChild>
          <button
            type="button"
            className="flex w-full items-center justify-between rounded-lg border border-slate-200/50 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm transition-all hover:border-primary-300 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
          >
            <span className="flex items-center gap-2">
              <CalendarIcon className="h-4 w-4 text-gray-400" />
              {displayValue}
            </span>
            <svg
              className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </Popover.Trigger>

        <Popover.Portal>
          <Popover.Content
            className="z-50 rounded-lg border border-slate-200/50 bg-white p-4 shadow-lg outline-none"
            align="start"
            sideOffset={8}
          >
            <DayPicker
              mode="single"
              selected={value}
              onSelect={handleDateSelect}
              disabled={[
                ...(minDate ? [{ before: minDate }] : []),
                ...(maxDate ? [{ after: maxDate }] : []),
              ]}
              className="rdp-custom"
              classNames={{
                root: 'text-sm',
                months: 'flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0',
                month: 'space-y-4',
                caption: 'flex justify-center pt-1 relative items-center',
                caption_label: 'text-sm font-medium text-gray-900',
                nav: 'space-x-1 flex items-center',
                button_previous: 'absolute left-1 h-7 w-7 bg-transparent p-0 inline-flex items-center justify-center rounded-md text-slate-600 hover:text-emerald-600 hover:bg-emerald-50/30 transition-colors',
                button_next: 'absolute right-1 h-7 w-7 bg-transparent p-0 inline-flex items-center justify-center rounded-md text-slate-600 hover:text-emerald-600 hover:bg-emerald-50/30 transition-colors',
                month_grid: 'w-full border-collapse space-y-1',
                weekdays: 'flex',
                weekday: 'text-slate-500 rounded-md w-9 font-normal text-[0.8rem]',
                week: 'flex w-full mt-2',
                day: 'h-9 w-9 text-center text-sm p-0 relative [&:has([data-selected])]:date-selected-cell',
                day_button: 'h-9 w-9 p-0 font-normal text-slate-700 rounded-md transition-colors focus:outline-none',
                selected: 'bg-emerald-500 text-white rounded-md',
                today: 'font-semibold text-slate-900',
                outside: 'text-slate-400 opacity-50',
                disabled: 'text-slate-400 opacity-50 cursor-not-allowed hover:!bg-transparent',
                range_middle: 'aria-selected:bg-accent aria-selected:text-accent-foreground',
                hidden: 'invisible',
              }}
            />

            {/* Time Picker */}
            {showTime && (
              <div className="mt-3 pt-3 border-t border-slate-200/50">
                <label className="flex items-center gap-2 text-xs font-medium text-gray-600 mb-2">
                  <Clock className="h-3.5 w-3.5" />
                  Time
                </label>
                <input
                  type="time"
                  value={timeValue}
                  onChange={handleTimeChange}
                  className="block w-full rounded-lg border border-slate-200/50 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm transition-all hover:border-primary-300 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                />
              </div>
            )}
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </div>
  );
};
